// Display a list of selectable items using checkboxes
import { Checkbox } from "@progress/kendo-react-inputs";
import { ListView, ListViewItemProps } from "@progress/kendo-react-listview";
import classNames from "classnames";
import * as React from "react";
import "./CPSelectListStyles.scss";
import { CPText } from "shared/components/base";

export interface CPSelectListItem {
  /** Optional unique key for the item. */
  key?: string | number;
  /** Display text rendered next to the checkbox. */
  text?: string;
  /** Whether the item is currently checked. */
  checked: boolean;
}

interface CPSelectListProps {
  /** Array of items to display in the list. */
  data: CPSelectListItem[];
  /** Callback fired when an item is clicked. Receives the clicked `CPSelectListItem`. */
  onSelect: (selectedItem: CPSelectListItem) => void;
}

/**
 * Scrollable list of checkbox items rendered with the Kendo `ListView`.
 * Each row contains a checkbox and label; clicking anywhere on the row fires `onSelect`.
 *
 * @example
 * ```tsx
 * const [items, setItems] = React.useState([
 *   { key: 1, text: 'Option A', checked: false },
 *   { key: 2, text: 'Option B', checked: true },
 * ]);
 *
 * const handleSelect = (item: CPSelectListItem) => {
 *   setItems(items.map(i => i.key === item.key ? { ...i, checked: !i.checked } : i));
 * };
 *
 * <CPSelectList data={items} onSelect={handleSelect} />
 * ```
 */
const CPSelectList: React.FC<CPSelectListProps> = (props) => {
  const renderItem = (itemProps: ListViewItemProps) => {
    let item = itemProps.dataItem;

    return (
      <div
        onClick={() => props.onSelect(item)}
        className={classNames("cp-select-list-item")}
      >
        <Checkbox value={item.checked} />
        <CPText>{item.text}</CPText>
      </div>
    );
  };

  return (
    <ListView data={props.data} item={renderItem} className="cp-select-list" />
  );
};

export default CPSelectList;
